import apiClient from './client'
import type { ErrorGroup, ErrorInstance, ErrorGroupStatus } from '../types'

export interface ErrorGroupsResponse {
  error_groups: ErrorGroup[]
  total: number
}

export interface TeamErrorGroup extends ErrorGroup {
  app_name: string
  app_id: string
}

export interface TeamErrorGroupsResponse {
  error_groups: TeamErrorGroup[]
  total: number
}

export interface ErrorInstancesResponse {
  errors: ErrorInstance[]
  total: number
}

export interface BreadcrumbEvent {
  error_id: string
  error_message: string | null
  type: string
  category: string | null
  message: string
  timestamp: string
  data: Record<string, unknown> | null
}

export interface BreadcrumbsResponse {
  breadcrumbs: BreadcrumbEvent[]
}

export interface MessageResponse {
  message: string
}

export const errorsApi = {
  /**
   * List all errors across all apps for the current team.
   */
  listTeamErrors: async (params?: {
    app_id?: string
    status?: ErrorGroupStatus
    environment?: string
    level?: string
    assigned_to?: string
    offset?: number
    limit?: number
  }): Promise<TeamErrorGroupsResponse> => {
    const response = await apiClient.get<TeamErrorGroupsResponse>('/errors', { params })
    return response.data
  },

  listGroups: async (
    appId: string,
    params?: {
      resolved?: boolean
      status?: ErrorGroupStatus
      environment?: string
      release?: string
      level?: string
      assigned_to?: string
      skip?: number
      limit?: number
    }
  ): Promise<ErrorGroupsResponse> => {
    const response = await apiClient.get<ErrorGroupsResponse>(`/apps/${appId}/errors`, {
      params,
    })
    return response.data
  },

  getGroup: async (appId: string, groupId: string): Promise<ErrorGroup> => {
    const response = await apiClient.get<ErrorGroup>(`/apps/${appId}/errors/${groupId}`)
    return response.data
  },

  listInstances: async (
    appId: string,
    groupId: string,
    params?: {
      offset?: number
      limit?: number
    }
  ): Promise<ErrorInstancesResponse> => {
    const response = await apiClient.get<ErrorInstancesResponse>(
      `/apps/${appId}/errors/${groupId}/occurrences`,
      { params }
    )
    return response.data
  },

  resolveGroup: async (appId: string, groupId: string): Promise<ErrorGroup> => {
    const response = await apiClient.post<ErrorGroup>(`/apps/${appId}/errors/${groupId}/resolve`)
    return response.data
  },

  unresolveGroup: async (appId: string, groupId: string): Promise<ErrorGroup> => {
    const response = await apiClient.post<ErrorGroup>(`/apps/${appId}/errors/${groupId}/unresolve`)
    return response.data
  },

  deleteGroup: async (appId: string, groupId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/apps/${appId}/errors/${groupId}`)
    return response.data
  },

  updateStatus: async (
    appId: string,
    groupId: string,
    status: ErrorGroupStatus
  ): Promise<ErrorGroup> => {
    const response = await apiClient.put<ErrorGroup>(`/apps/${appId}/errors/${groupId}/status`, {
      status,
    })
    return response.data
  },

  assign: async (
    appId: string,
    groupId: string,
    assignedTo: string | null
  ): Promise<ErrorGroup> => {
    const response = await apiClient.put<ErrorGroup>(`/apps/${appId}/errors/${groupId}/assign`, {
      assigned_to: assignedTo,
    })
    return response.data
  },

  mute: async (
    appId: string,
    groupId: string,
    options: { hours?: number; until_count?: number }
  ): Promise<ErrorGroup> => {
    const response = await apiClient.post<ErrorGroup>(
      `/apps/${appId}/errors/${groupId}/mute`,
      options
    )
    return response.data
  },

  bulkUpdateStatus: async (
    appId: string,
    errorGroupIds: string[],
    status: ErrorGroupStatus
  ): Promise<{ updated_count: number; status: string }> => {
    const response = await apiClient.put<{ updated_count: number; status: string }>(
      `/apps/${appId}/errors/bulk/status`,
      { error_group_ids: errorGroupIds, status }
    )
    return response.data
  },

  getRecentBreadcrumbs: async (
    appId: string,
    params?: { hours?: number; limit?: number }
  ): Promise<BreadcrumbsResponse> => {
    const response = await apiClient.get<BreadcrumbsResponse>(`/apps/${appId}/errors/breadcrumbs`, {
      params,
    })
    return response.data
  },
}

export type { ErrorGroup, ErrorInstance }
